import React, { useEffect, useMemo } from 'react'
import ListPagination from 'Components/ListPagination/ListPagination'
import type { ListPageSize } from 'Hooks/useListPagination'
import {
  StationAssignment,
  StationStaffDirectoryUser,
  StationStaffRole,
} from 'BoostXpressAdmin/Services/stationStaffService'

const formatRole = (role: StationStaffRole) =>
  role === 'STATION_MANAGER' ? 'Manager' : 'Associate'

const formatDate = (value: string) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString()
}

type Props = {
  users: StationStaffDirectoryUser[]
  assignments: StationAssignment[]
  stationLabels: Record<string, string>
  isLoading: boolean
  error: string
  page: number
  limit: number
  searchTerm: string
  roleFilter: '' | StationStaffRole
  removingAssignmentId: string
  updatingUserId: string
  onPageChange: (page: number) => void
  onPageSizeChange: (limit: ListPageSize) => void
  onSearchTermChange: (value: string) => void
  onRoleFilterChange: (value: '' | StationStaffRole) => void
  onAddUser: () => void
  onManageStation: (stationId: string) => void
  onChooseStation: () => void
  onRemoveAssignment: (assignment: StationAssignment) => void
  onToggleUserStatus: (user: StationStaffDirectoryUser, hasAssignment: boolean) => void
  onRetry: () => void
}

type StationStaffRow = {
  key: string
  userResourceId: string
  firstName: string
  lastName: string
  email: string
  role: StationStaffRole
  assignment?: StationAssignment
  user?: StationStaffDirectoryUser
}

const StationStaffTable = ({
  users,
  assignments,
  stationLabels,
  isLoading,
  error,
  page,
  limit,
  searchTerm,
  roleFilter,
  removingAssignmentId,
  updatingUserId,
  onPageChange,
  onPageSizeChange,
  onSearchTermChange,
  onRoleFilterChange,
  onAddUser,
  onManageStation,
  onChooseStation,
  onRemoveAssignment,
  onToggleUserStatus,
  onRetry,
}: Props) => {
  const normalizedSearch = searchTerm.trim().toLowerCase()

  const staffRows = useMemo(() => {
    const rows = users.flatMap<StationStaffRow>((user) => {
      const userAssignments = assignments.filter(
        (assignment) =>
          assignment.userResourceId === user.resourceId && assignment.role === user.accountType,
      )

      if (!userAssignments.length) {
        return [
          {
            key: `user-${user.resourceId}`,
            userResourceId: user.resourceId,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.accountType,
            assignment: undefined,
            user,
          },
        ]
      }

      return userAssignments.map((assignment) => ({
        key: assignment.resourceId,
        userResourceId: user.resourceId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.accountType,
        assignment,
        user,
      }))
    })

    const knownUserIds = new Set(users.map((user) => user.resourceId))
    const assignmentFallbackRows: StationStaffRow[] = assignments
      .filter((assignment) => !knownUserIds.has(assignment.userResourceId))
      .map((assignment) => ({
        key: assignment.resourceId,
        userResourceId: assignment.userResourceId,
        firstName: assignment.userFirstName,
        lastName: assignment.userLastName,
        email: assignment.userEmail,
        role: assignment.role,
        assignment,
      }))

    return [...rows, ...assignmentFallbackRows]
  }, [assignments, users])

  const filteredRows = useMemo(
    () =>
      staffRows.filter((row) => {
        const matchesRole = !roleFilter || row.role === roleFilter
        const stationId = row.assignment?.stationId ?? ''
        const matchesSearch =
          !normalizedSearch ||
          `${row.firstName} ${row.lastName}`.toLowerCase().includes(normalizedSearch) ||
          row.email.toLowerCase().includes(normalizedSearch) ||
          stationId.toLowerCase().includes(normalizedSearch) ||
          (stationLabels[stationId] ?? '').toLowerCase().includes(normalizedSearch)

        return matchesRole && matchesSearch
      }),
    [normalizedSearch, roleFilter, staffRows, stationLabels],
  )

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit))
  const visibleRows = filteredRows.slice((page - 1) * limit, page * limit)

  useEffect(() => {
    if (page > totalPages) onPageChange(totalPages)
  }, [onPageChange, page, totalPages])

  return (
    <section className='bx-staff-table'>
      <div className='bx-staff-table__header'>
        <div>
          <h3 className='bx-staff-table__title'>Station Staff</h3>
          <p className='bx-staff-table__subtitle'>{users.length} staff accounts</p>
        </div>
        <button type='button' className='bx-staff-table__primary-btn' onClick={onAddUser}>
          + Add User
        </button>
      </div>

      <div className='bx-staff-table__toolbar'>
        <label className='bx-staff-search'>
          <span className='bx-staff-search__icon' aria-hidden='true'>
            <svg width='17' height='17' viewBox='0 0 24 24' fill='none'>
              <path
                d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </span>
          <span className='visually-hidden'>Search station staff</span>
          <input
            className='bx-staff-search__input'
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder='Search by name, email, or station'
          />
        </label>

        <label className='bx-staff-filter'>
          <span className='visually-hidden'>Filter station staff role</span>
          <select
            className='bx-staff-filter__select'
            value={roleFilter}
            onChange={(event) => onRoleFilterChange(event.target.value as '' | StationStaffRole)}
          >
            <option value=''>All roles</option>
            <option value='STATION_MANAGER'>Managers</option>
            <option value='STATION_ASSOCIATE'>Associates</option>
          </select>
        </label>
      </div>

      <div className='bx-staff-table__card'>
        {error ? (
          <div className='bx-staff-table__state bx-staff-table__state--error'>
            <p>{error}</p>
            <button type='button' onClick={onRetry}>
              Retry
            </button>
          </div>
        ) : (
          <div className='bx-staff-table__responsive'>
            <table className='table bx-staff-table__table align-middle mb-0'>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Station</th>
                  <th>Assigned</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className='bx-staff-table__state-cell'>
                      Loading station staff...
                    </td>
                  </tr>
                ) : visibleRows.length ? (
                  visibleRows.map((row, index) => (
                    <tr key={row.key}>
                      <td data-label='#'>{(page - 1) * limit + index + 1}</td>
                      <td data-label='Name' className='bx-staff-table__name'>
                        {`${row.firstName} ${row.lastName}`.trim() || '—'}
                      </td>
                      <td data-label='Email' className='bx-staff-table__truncate'>
                        {row.email}
                      </td>
                      <td data-label='Role'>
                        <span
                          className={`bx-staff-role bx-staff-role--${row.role === 'STATION_MANAGER' ? 'manager' : 'associate'}`}
                        >
                          {formatRole(row.role)}
                        </span>
                      </td>
                      <td data-label='Station' className='bx-staff-table__station'>
                        {row.assignment
                          ? (stationLabels[row.assignment.stationId] ?? row.assignment.stationId)
                          : 'Unassigned'}
                      </td>
                      <td data-label='Assigned'>
                        {row.assignment ? formatDate(row.assignment.createdAt) : '—'}
                      </td>
                      <td data-label='Status'>
                        {row.user ? (
                          <span
                            className={`bx-status-pill bx-status-pill--${row.user.disabledAt ? 'inactive' : 'active'}`}
                          >
                            {row.user.disabledAt ? 'Disabled' : 'Active'}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td data-label='Actions'>
                        <div
                          className={`bx-staff-table__actions${
                            row.assignment ? ' bx-staff-table__actions--assigned' : ''
                          }`}
                        >
                          {row.assignment ? (
                            <>
                              <button
                                type='button'
                                className='bx-staff-table__action bx-staff-table__action--manage'
                                onClick={() => {
                                  if (row.assignment) onManageStation(row.assignment.stationId)
                                }}
                              >
                                Manage station
                              </button>
                              <button
                                type='button'
                                className='bx-staff-table__action bx-staff-table__action--danger'
                                disabled={removingAssignmentId === row.assignment.resourceId}
                                onClick={() => {
                                  if (row.assignment) onRemoveAssignment(row.assignment)
                                }}
                              >
                                {removingAssignmentId === row.assignment.resourceId
                                  ? 'Removing...'
                                  : 'Remove'}
                              </button>
                            </>
                          ) : (
                            <button
                              type='button'
                              className='bx-staff-table__action'
                              onClick={onChooseStation}
                            >
                              Choose station
                            </button>
                          )}
                          {row.user ? (
                            <button
                              type='button'
                              className={`bx-staff-table__action${
                                row.user.disabledAt
                                  ? ' bx-staff-table__action--success'
                                  : ' bx-staff-table__action--danger'
                              }`}
                              disabled={updatingUserId === row.userResourceId}
                              onClick={() => {
                                if (row.user) onToggleUserStatus(row.user, Boolean(row.assignment))
                              }}
                            >
                              {updatingUserId === row.userResourceId
                                ? 'Updating...'
                                : row.user.disabledAt
                                  ? 'Enable'
                                  : 'Disable'}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className='bx-staff-table__state-cell'>
                      {staffRows.length
                        ? 'No station staff match the selected filters.'
                        : 'No station staff accounts found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <ListPagination
          page={page}
          limit={limit}
          total={filteredRows.length}
          disabled={isLoading || Boolean(error)}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    </section>
  )
}

export default StationStaffTable
